/** @jsxRuntime automatic */
/** @jsxImportSource preact */
// Extra shared UI primitives (FRONTEND-DIALER owns this file; FRONTEND-ADMIN may import from it).
// Base primitives (Modal, Toasts, ErrorBanner, Empty, fmtDateTime…) live in ./ui.tsx. The pragmas above let Vitest render
// LinkOut inside components it tests (tests/fd-lead-links.test.ts); the app build is Preact either way.
import '../dialer/dialer.css';
import type { ComponentChildren } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { errorMessage } from '../api';

/** Loads data on mount / when `deps` change; ignores responses that arrive after a newer request started. */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[]): {
  data: T | undefined; error: string | null; loading: boolean; reload: () => void; setData: (v: T) => void;
} {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);
  const run = useCallback(() => {
    const mine = ++seq.current;
    setLoading(true);
    load().then(
      (v) => { if (mine === seq.current) { setData(v); setError(null); setLoading(false); } },
      (e) => { if (mine === seq.current) { setError(errorMessage(e)); setLoading(false); } },
    );
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, [run]);
  return { data, error, loading, reload: run, setData };
}

/** Current time, re-rendering every `ms` (clocks, call timers). */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

/** Subscribes a component to a tiny external store (the dialer store, the phone manager). */
export function useExternal<T>(subscribe: (fn: () => void) => () => void, get: () => T): T {
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((n) => n + 1)), [subscribe]);
  return get();
}

/** Debounced copy of a value (search boxes). */
export function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function Kbd(props: { children: ComponentChildren }) {
  return <kbd>{props.children}</kbd>;
}

export function Spinner(props: { label?: string }) {
  return <span class="vd-spinner" role="status" aria-label={props.label ?? 'Loading'} />;
}

/** Status dot + text; tone picks the colour token. */
export function Dot(props: { tone: 'ok' | 'warn' | 'bad' | 'info' | 'idle' }) {
  return <span class={`vd-dot vd-dot-${props.tone}`} aria-hidden="true" />;
}

/** External link that never leaks the dialer URL or window. */
export function LinkOut(props: { href: string | null | undefined; children: ComponentChildren; class?: string; title?: string }) {
  if (!props.href) return null;
  const safe = /^https?:\/\//i.test(props.href) ? props.href : null;
  if (!safe) return <span class={props.class} title="Not a web link">{props.children}</span>;
  return <a href={safe} target="_blank" rel="noopener noreferrer" class={props.class} title={props.title}>{props.children}</a>;
}

export function Field(props: { label: string; hint?: string; children: ComponentChildren; class?: string }) {
  return (
    <label class={`vd-field ${props.class ?? ''}`}>
      <span class="vd-field-label">{props.label}</span>
      {props.children}
      {props.hint ? <span class="vd-field-hint">{props.hint}</span> : null}
    </label>
  );
}

/** Right-hand drawer (lead detail). Esc or the backdrop closes it. */
export function Drawer(props: { title: string; onClose: () => void; children: ComponentChildren; width?: number }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') props.onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [props.onClose]);
  return (
    <div class="vd-drawer-backdrop" onClick={(e) => { if (e.target === e.currentTarget) props.onClose(); }}>
      <aside class="vd-drawer" style={`max-width:${props.width ?? 620}px`} role="dialog" aria-label={props.title}>
        <div class="row" style="justify-content:space-between;margin-bottom:12px">
          <h2 style="margin:0">{props.title}</h2>
          <button class="btn btn-sm" onClick={props.onClose} aria-label="Close">Esc</button>
        </div>
        {props.children}
      </aside>
    </div>
  );
}

/** Tabs as a segmented control. */
export function Segmented<T extends string>(props: { value: T; options: Array<{ value: T; label: string; count?: number }>; onChange: (v: T) => void }) {
  return (
    <div class="vd-seg" role="tablist">
      {props.options.map((o) => (
        <button role="tab" aria-selected={o.value === props.value} class={o.value === props.value ? 'on' : ''} onClick={() => props.onChange(o.value)}>
          {o.label}{o.count !== undefined ? <span class="vd-seg-count">{o.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** A two-step confirm used for irreversible actions (add to DNC, cancel meeting). */
export function ConfirmButton(props: {
  label: string; confirmLabel: string; onConfirm: () => Promise<void> | void; class?: string; disabled?: boolean; title?: string;
}) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 5000);
    return () => clearTimeout(t);
  }, [armed]);
  if (!armed) {
    return <button class={props.class ?? 'btn btn-sm'} disabled={props.disabled} title={props.title} onClick={() => setArmed(true)}>{props.label}</button>;
  }
  return (
    <span class="row" style="gap:6px">
      <button class="btn btn-sm btn-danger" disabled={busy} onClick={async () => {
        setBusy(true);
        try { await props.onConfirm(); } finally { setBusy(false); setArmed(false); }
      }}>{busy ? 'Working…' : props.confirmLabel}</button>
      <button class="btn btn-sm" onClick={() => setArmed(false)}>Cancel</button>
    </span>
  );
}

/** '2026-09-24' for a Date in the browser's own zone (date inputs). */
export function isoDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
